/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.feature.loans.ui

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kpt.core.base.store.screen.ScreenState
import kpt.core.base.store.submit.SubmitOutbox
import kpt.core.base.ui.viewmodel.BaseMutationViewModel
import kpt.core.base.ui.viewmodel.MutationMode
import kpt.core.data.banking.LoanRepository
import kpt.core.model.banking.Loan
import kpt.core.model.banking.LoanKind
import kotlin.random.Random
import kotlin.time.Clock

/**
 * **The canonical multi-formKey [BaseMutationViewModel] (`MutationMode.Draft`) showcase.**
 *
 * Each instance is scoped to a single loan via `uniqueKey = loanId`. The user can edit three
 * loans offline → three independent PENDING drafts coexist in `framework_submit_drafts`. On
 * the next online transition, [kpt.core.base.store.submit.OfflineSubmitSyncer] commits
 * them serially — none lost, none overwritten.
 *
 * `formKey = "loan"` groups every loan draft under one form-type for outbox queries (e.g.
 * "show me all pending loan drafts on the dashboard"). `uniqueKey` partitions inside the
 * form-type so concurrent drafts coexist.
 *
 * **Add vs Edit modes:**
 * - `loanId == null` → fresh add. `uniqueKey = "new"` (singleton add slot).
 * - `loanId != null` → edit existing. Form pre-populated via [hydrateFromExisting].
 *
 * The "submit" target is `repository.upsert(loan)` — purely local. The DraftSubmitHandler
 * polish (Saving… / Saved ✓ / Saved offline) still applies, which is the whole point of
 * the showcase: forks see the framework's offline-resilience UX even when their submit is
 * a local commit, and can swap in a remote API call later with zero ViewModel changes.
 */
class EditLoanViewModel(
    private val repository: LoanRepository,
    outbox: SubmitOutbox<Loan>,
    /**
     * The loan being edited, or null when adding. Also the draft's unique key, so an edit draft never collides with
     * the add slot.
     */
    val loanId: String?,
) : BaseMutationViewModel<Loan, Loan>(
    MutationMode.Draft(
        outbox = outbox,
        formKey = FORM_KEY,
        uniqueKey = loanId ?: NEW_LOAN_KEY,
        autoSaveDraft = true,
    ),
) {

    private val _formState = MutableStateFlow(LoanFormState())

    /** The form as it currently stands. */
    val formState: StateFlow<LoanFormState> = _formState.asStateFlow()

    init {
        // `mutableScreenState` only gates "form ready" — Loading while an existing loan hydrates
        // (edit), then Content(snapshot); Content(snapshot) immediately for create. Without this the
        // screen would sit at ScreenState.Loading forever (MutationScreenContent renders the read
        // gate). The content slot ignores the read payload (the form renders `_formState`), so the
        // snapshot is a "ready" sentinel, not display data.
        if (loanId != null) {
            viewModelScope.launch {
                // One-shot hydrate THROUGH the store — take the first SETTLED value off the
                // store-backed detail stream rather than a raw `repository.getById`. The one-shot
                // semantics that matter here are preserved (a live stream would clobber the user's
                // in-progress edits on every unrelated write), but the read now goes through
                // Store5: same SoT, same cache, same error path as every other read in the app.
                val settled = repository.loanDetailStream(loanId, viewModelScope).state
                    .firstOrNull { it is ScreenState.Content<Loan> || it is ScreenState.Empty }
                (settled as? ScreenState.Content<Loan>)?.data?.let { hydrateFromExisting(it) }
                mutableScreenState.value = ScreenState.Content(formSnapshot())
            }
        } else {
            mutableScreenState.value = ScreenState.Content(formSnapshot())
        }
    }

    /**
     * Three-case Resume: pre-fill the form from the saved draft surfaced in
     * [uiState]`.resumableDraft`, then dismiss the prompt (the inherited [onResumeDraft]).
     * Wired to [kpt.core.base.ui.draft.DraftResolutionPrompt]'s Resume action.
     */
    fun onResume() {
        uiState.value.resumableDraft?.let { hydrateFromExisting(it) }
        onResumeDraft()
    }

    /** A [Loan] snapshot of the current form — the "form ready" sentinel for the screen state. */
    private fun formSnapshot(): Loan {
        val form = _formState.value
        val now = Clock.System.now().toEpochMilliseconds()
        return Loan(
            id = loanId ?: NEW_LOAN_KEY,
            name = form.name,
            kind = form.kind,
            principal = form.principal,
            principalRemaining = form.principalRemaining,
            annualRatePercent = form.annualRatePercent,
            tenureMonths = form.tenureMonths,
            monthsRemaining = form.monthsRemaining,
            monthlyPayment = computeMonthlyEmi(
                principal = form.principal,
                annualRatePercent = form.annualRatePercent,
                tenureMonths = form.tenureMonths,
            ),
            nextDueDate = form.nextDueDate,
            totalPaid = form.totalPaid,
            createdAtMs = now,
            updatedAtMs = now,
        )
    }

    /** Pre-populate the form with the existing loan's fields. Idempotent. */
    private fun hydrateFromExisting(loan: Loan) {
        _formState.value = LoanFormState(
            name = loan.name,
            kind = loan.kind,
            principal = loan.principal,
            principalRemaining = loan.principalRemaining,
            annualRatePercent = loan.annualRatePercent,
            tenureMonths = loan.tenureMonths,
            monthsRemaining = loan.monthsRemaining,
            nextDueDate = loan.nextDueDate,
            totalPaid = loan.totalPaid,
        )
    }

    /**
     * Records a new label into the form, which auto-saves as a draft — so the edit survives process death before it is
     * ever committed.
     */
    fun onNameChange(value: String) {
        _formState.update { it.copy(name = value) }
    }

    /** Category changed. */
    fun onKindChange(value: LoanKind) {
        _formState.update { it.copy(kind = value) }
    }

    /**
     * Records a new principal. Does NOT recompute [LoanFormState.principalRemaining]: the tracker treats the
     * outstanding balance as user-maintained.
     */
    fun onPrincipalChange(value: Double) {
        _formState.update {
            it.copy(
                principal = value,
                // For new loans, keep remaining in sync with principal until the user edits it.
                principalRemaining = if (loanId == null) value else it.principalRemaining,
            )
        }
    }

    /** Outstanding balance changed. */
    fun onPrincipalRemainingChange(value: Double) {
        _formState.update { it.copy(principalRemaining = value) }
    }

    /** APR changed. */
    fun onAnnualRatePercentChange(value: Double) {
        _formState.update { it.copy(annualRatePercent = value) }
    }

    /** Records a new tenure. Independent of months-remaining, which the user maintains separately. */
    fun onTenureMonthsChange(value: Int) {
        _formState.update {
            it.copy(
                tenureMonths = value,
                monthsRemaining = if (loanId == null) value else it.monthsRemaining,
            )
        }
    }

    /**
     * Records a new months-remaining. User-maintained rather than derived — the tracker has no payment ledger to
     * derive it from.
     */
    fun onMonthsRemainingChange(value: Int) {
        _formState.update { it.copy(monthsRemaining = value) }
    }

    /**
     * Records a new due-date. Stored as a `LocalDate`, so no timezone is involved and the date a user picks is the
     * date they see.
     */
    fun onNextDueDateChange(value: LocalDate) {
        _formState.update { it.copy(nextDueDate = value) }
    }

    /** Amount paid to date changed. */
    fun onTotalPaidChange(value: Double) {
        _formState.update { it.copy(totalPaid = value) }
    }

    /**
     * Snapshot the current form into a [Loan] payload and submit through the draft handler.
     *
     * Blank name or non-positive principal → no-op. Computes monthly EMI from
     * (principal, annualRatePercent, tenureMonths) so the persisted row carries an
     * accurate dashboard value even before the user logs any payments.
     */
    fun onSubmit() {
        val form = _formState.value
        if (!form.isValid) return
        val now = Clock.System.now().toEpochMilliseconds()
        val monthly = computeMonthlyEmi(
            principal = form.principal,
            annualRatePercent = form.annualRatePercent,
            tenureMonths = form.tenureMonths,
        )
        val payload = Loan(
            id = loanId ?: randomId(),
            name = form.name.trim(),
            kind = form.kind,
            principal = form.principal,
            principalRemaining = form.principalRemaining,
            annualRatePercent = form.annualRatePercent,
            tenureMonths = form.tenureMonths,
            monthsRemaining = form.monthsRemaining,
            monthlyPayment = monthly,
            nextDueDate = form.nextDueDate,
            totalPaid = form.totalPaid,
            createdAtMs = now,
            updatedAtMs = now,
        )
        super.onSubmit(payload)
    }

    /** Local commit. With the syncer wired, this also runs during offline-draft replay. */
    override suspend fun performSubmit(payload: Loan): Loan {
        repository.upsert(payload)
        return payload
    }

    /** Reset both submit state (super) and form (VM-local). */
    override fun onDismissResult() {
        super.onDismissResult()
        _formState.value = LoanFormState()
    }

    private fun randomId(): String {
        val chars = ('a'..'z') + ('0'..'9')
        return (1..16).map { chars[Random.nextInt(chars.size)] }.joinToString("")
    }

    /** Outbox keys. */
    companion object {
        /** Stable form-type identifier — groups every loan draft in the outbox. */
        const val FORM_KEY: String = "loan"

        /**
         * Sentinel [MutationMode.Draft.uniqueKey] for the "add new loan" slot when
         * no [loanId] is supplied. Per-loan edit drafts use the loan id itself, so they never
         * collide with the add slot.
         */
        const val NEW_LOAN_KEY: String = "new"
    }
}

/**
 * Multi-field form state for the add/edit loan screen.
 *
 * Default values render a sensible "blank form" — kind defaults to PERSONAL, dates default
 * to a stable epoch so the form is deterministic in tests. The Compose layer translates
 * 0.0 → empty text-field, "Pick date" → date picker prompt.
 */
data class LoanFormState(
    /** User-facing label. */
    val name: String = "",
    /** Loan category. Drives the row icon and grouping; it carries no financial meaning. */
    val kind: LoanKind = LoanKind.PERSONAL,
    /** Original loan amount. */
    val principal: Double = 0.0,
    /** Outstanding balance. */
    val principalRemaining: Double = 0.0,
    /** APR as a percentage. */
    val annualRatePercent: Double = 0.0,
    /** Tenure the loan was taken for, in months. Fixed — [monthsRemaining] is what moves. */
    val tenureMonths: Int = 0,
    /** Months until payoff. */
    val monthsRemaining: Int = 0,
    /**
     * Next payment due-date. Defaults to a fixed placeholder so the form renders deterministically in tests and
     * previews rather than drifting with today's date.
     */
    val nextDueDate: LocalDate = DEFAULT_DUE_DATE,
    /** Amount paid to date. */
    val totalPaid: Double = 0.0,
) {
    /** Minimum validity for the Save button. */
    val isValid: Boolean
        get() = name.isNotBlank() &&
            principal > 0.0 &&
            tenureMonths > 0 &&
            annualRatePercent >= 0.0

    /** Live preview of the monthly payment — surfaced read-only on the form. */
    val previewMonthlyEmi: Double
        get() = computeMonthlyEmi(principal, annualRatePercent, tenureMonths)

    /** Live preview of total interest over the loan tenure — surfaced read-only on the form. */
    val previewTotalInterest: Double
        get() = computeTotalInterest(principal, annualRatePercent, tenureMonths)

    /** Form defaults. */
    companion object {
        /** Stable placeholder so the form is deterministic before the user picks a real date. */
        val DEFAULT_DUE_DATE: LocalDate = LocalDate(2026, 1, 1)
    }
}
